import { useState } from "react";
import { useSearchParams } from "react-router";
import { Button, Card, MagnusOperaIcon, ThemeToggle } from "@insights/ui";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { authClient } from "../../../api";
import "../styles/connect.css";

export function ConnectPage() {
  const [params] = useSearchParams();
  const code = params.get("user_code") ?? "";
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  async function respond(approve: boolean) {
    setBusy(true);
    setMessage("");
    try {
      const verified = await authClient.device({ query: { user_code: code } });
      if (verified.error) throw new Error(verified.error.error_description);
      const result = approve
        ? await authClient.device.approve({ userCode: code })
        : await authClient.device.deny({ userCode: code });
      if (result.error) throw new Error(result.error.error_description);
      setDone(true);
      setMessage(
        approve
          ? "FeatStudio2 is connected. You can return to the application."
          : "Connection declined.",
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Connection failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="featstudio-connect-shell">
      <ThemeToggle className="auth-theme" />
      <Card className="featstudio-connect-card" aria-labelledby="connect-title">
        <div className="mark">
          <MagnusOperaIcon /> Insights
        </div>
        <header className="featstudio-connect-heading">
          <h1 id="connect-title">{done ? "You’re all set" : "Connect FeatStudio2"}</h1>
          <p>Bring your engineering triage into your Insights workspace.</p>
        </header>
        {!done && (
          <>
            <section className="featstudio-connect-code" aria-label="Verification code">
              <span>Check this code in FeatStudio2</span>
              <strong>{code || "No code provided"}</strong>
              <p>Only continue if you requested this connection and the codes match.</p>
            </section>
            <div className="featstudio-connect-permissions">
              <ShieldCheck aria-hidden="true" size={20} />
              <p>
                Connect using your Insights identity. Your workspace permissions
                still apply, and FeatStudio2 must be enabled for the workspace.
              </p>
            </div>
          </>
        )}
        {(message || !code) && (
          <div className={`featstudio-connect-status${done ? "" : " error"}`} role="status">
            {done && <CheckCircle2 aria-hidden="true" size={24} />}
            <p>{message || "This link is missing a verification code. Start a new connection from FeatStudio2."}</p>
          </div>
        )}
        {!done && (
          <div className="featstudio-connect-actions">
            <Button
              color="neutral"
              disabled={busy || !code}
              onClick={() => void respond(false)}
            >
              Decline
            </Button>
            <Button disabled={busy || !code} onClick={() => void respond(true)}>
              {busy ? "Please wait…" : "Connect FeatStudio2"}
            </Button>
          </div>
        )}
      </Card>
    </main>
  );
}
